import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import PropTypes from "prop-types"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Bounce, toast } from "react-toastify"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import ListingTabs from "../../../components/Listing/ListingTabs"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import { ICONS } from "../../../constants/icons"
import { get, patch, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getApiFieldErrors } from "../../../helpers/formFieldErrors"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { formatDisplayName } from "../../../helpers/userDisplay"
import { ADMIN_ME_WITH } from "../../../helpers/adminMe"
import { setUser, setUserPic } from "../../../store/slices/authSlice"
import {
  buildDefaultNotificationPrefs,
  mapNotificationSettingsFromApi,
  mapNotificationSettingsToApi,
  NOTIFICATION_SECTIONS,
  SETTINGS_TABS,
} from "./adminSettingsData"
import ChangePasswordModal from "./ChangePasswordModal"
import NotificationsTab from "./NotificationsTab"
import ProfileSettingsTab from "./ProfileSettingsTab"

const VALID_TABS = SETTINGS_TABS.map(tab => tab.key)

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const formatLocation = user => {
  if (!user) return ""
  if (typeof user.location === "string" && user.location.trim()) {
    return user.location.trim()
  }
  if (typeof user.city === "string" && user.city.trim()) {
    return user.city.trim()
  }

  const addresses = Array.isArray(user.addresses) ? user.addresses : []
  const primary =
    addresses.find(address => address?.type === "primary") || addresses[0]
  const city = primary?.city || user.city || user.active_city || null
  if (!city || typeof city === "string") return city || ""

  const name = city.city || city.name || ""
  const state = city.state || ""
  if (name && state) return `${name}, ${state}`
  return name || ""
}

const mapUserToProfile = (user = {}) => ({
  firstName: user.first_name || "",
  lastName: user.last_name || "",
  location: formatLocation(user),
  email: user.email || "",
  phone: user.phone || user.phone_number || "",
  twoFactorEnabled: Boolean(
    user.two_factor_enabled ??
      user.is_two_factor_enabled ??
      user.two_factor ??
      false,
  ),
})

/**
 * Shared Settings shell — Admin / City Admin Profile & Notifications.
 * Profile update + change password use the same member APIs:
 * `POST /api/v1/user/basic-profile`, `POST /api/v1/user/update/password`.
 */
const AdminSettings = ({
  basePath = "/admin/settings",
  documentTitle = "Settings | AMOK . EARTH Admin",
  notificationSections = NOTIFICATION_SECTIONS,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { tab } = useParams()
  const currentUser = useSelector(state => state.auth.currentUser)
  const activeTab = VALID_TABS.includes(tab) ? tab : "profile"
  const [profile, setProfile] = useState(() => mapUserToProfile(currentUser))
  const [notificationPrefs, setNotificationPrefs] = useState(() =>
    buildDefaultNotificationPrefs(notificationSections),
  )
  const [photoPreview, setPhotoPreview] = useState(null)
  const [profileImageFile, setProfileImageFile] = useState(null)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(true)
  // Start loading when landing on this tab so defaults never flash before API prefs.
  const [loadingNotifications, setLoadingNotifications] = useState(
    () => activeTab === "notifications",
  )
  const [updatingTwoFactor, setUpdatingTwoFactor] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})

  const avatarFromUser = useMemo(
    () => getUserAvatarUrl(currentUser),
    [currentUser],
  )

  const loadProfile = useCallback(async () => {
    setLoadingProfile(true)
    try {
      const response = await get(endpoints.getMe, {
        params: { "with[]": [...ADMIN_ME_WITH, "addresses.city"] },
      })
      if (response?.data?.success) {
        const user = response.data.result || {}
        const avatarUrl = getUserAvatarUrl(user)
        dispatch(setUser(user))
        if (avatarUrl) dispatch(setUserPic(avatarUrl))
        setProfile(mapUserToProfile(user))
        setPhotoPreview(avatarUrl || null)
        setProfileImageFile(null)
      } else if (response) {
        toastApiError(response, "Failed to load profile")
      }
    } catch (err) {
      toastApiError(err, "Failed to load profile")
    } finally {
      setLoadingProfile(false)
    }
  }, [dispatch])

  const loadNotificationSettings = useCallback(async () => {
    setLoadingNotifications(true)
    try {
      const response = await get(endpoints.getNotificationSettings)
      if (response?.data?.success) {
        setNotificationPrefs(
          mapNotificationSettingsFromApi(response.data.result || {}),
        )
      } else if (response) {
        toastApiError(response, "Failed to load notification settings")
      }
    } catch (err) {
      toastApiError(err, "Failed to load notification settings")
    } finally {
      setLoadingNotifications(false)
    }
  }, [])

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  // Before paint: hide switches until GET prefs finish (avoids default → API flicker).
  useLayoutEffect(() => {
    if (activeTab === "notifications") {
      setLoadingNotifications(true)
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === "notifications") {
      loadNotificationSettings()
    }
  }, [activeTab, loadNotificationSettings])
  useEffect(
    () => () => {
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview)
      }
    },
    [photoPreview],
  )

  if (tab && !VALID_TABS.includes(tab)) {
    return <Navigate to={`${basePath}/profile`} replace />
  }

  const handleTabChange = nextTab => {
    navigate(`${basePath}/${nextTab}`)
  }

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }))
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: "" }))
    }
  }

  const handlePhotoChange = (file, errorMessage) => {
    if (errorMessage) {
      setFieldErrors(prev => ({ ...prev, photo: errorMessage }))
      return
    }
    setFieldErrors(prev => ({ ...prev, photo: "" }))

    if (photoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview)
    }

    if (file) {
      setProfileImageFile(file)
      setPhotoPreview(URL.createObjectURL(file))
      return
    }

    setProfileImageFile(null)
    setPhotoPreview(avatarFromUser || null)
  }

  const handleNotificationToggle = async (key, enabled) => {
    if (saving || loadingNotifications) return

    const previousPrefs = notificationPrefs
    const nextPrefs = { ...previousPrefs, [key]: enabled }
    setNotificationPrefs(nextPrefs)
    setSaving(true)

    try {
      const response = await patch(
        endpoints.updateNotificationSettings,
        mapNotificationSettingsToApi(nextPrefs),
      )

      if (response?.data?.success) {
        const result = response.data.result
        if (result && typeof result === "object") {
          setNotificationPrefs(mapNotificationSettingsFromApi(result))
        }
        toast.success(
          "Notification settings updated successfully.",
          TOAST_OPTIONS,
        )
      } else if (response) {
        setNotificationPrefs(previousPrefs)
        toastApiError(response, "Failed to update notification settings")
      }
    } catch (err) {
      setNotificationPrefs(previousPrefs)
      toastApiError(err, "Failed to update notification settings")
    } finally {
      setSaving(false)
    }
  }

  const handleManageTwoFactor = async () => {
    if (updatingTwoFactor) return

    const nextEnabled = !profile.twoFactorEnabled
    setUpdatingTwoFactor(true)
    try {
      const response = await post(endpoints.updateTwoFactor, {
        enabled: nextEnabled,
      })

      if (response?.data?.success) {
        const result = response.data.result
        const enabledFromApi =
          typeof result?.enabled === "boolean"
            ? result.enabled
            : typeof result?.two_factor_enabled === "boolean"
              ? result.two_factor_enabled
              : nextEnabled

        setProfile(prev => ({ ...prev, twoFactorEnabled: enabledFromApi }))

        if (currentUser) {
          dispatch(
            setUser({
              ...currentUser,
              two_factor_enabled: enabledFromApi,
              is_two_factor_enabled: enabledFromApi,
              ...(result && typeof result === "object" ? result : {}),
            }),
          )
        }

        toast.success(
          response.data.message ||
            (enabledFromApi
              ? "Two-factor authentication enabled."
              : "Two-factor authentication disabled."),
          TOAST_OPTIONS,
        )
      } else if (response) {
        toastApiError(response, "Failed to update two-factor authentication")
      }
    } catch (err) {
      toastApiError(err, "Failed to update two-factor authentication")
    } finally {
      setUpdatingTwoFactor(false)
    }
  }

  const handleSave = async () => {
    if (saving) return

    if (!profile.firstName.trim()) {
      setFieldErrors({ firstName: "First name is required." })
      return
    }
    setFieldErrors({})

    setSaving(true)
    try {
      const formData = new FormData()
      formData.append(
        "first_name",
        formatDisplayName(profile.firstName.trim()),
      )
      formData.append(
        "last_name",
        formatDisplayName(profile.lastName?.trim() || ""),
      )
      if (profile.phone?.trim()) {
        formData.append("phone_number", profile.phone.trim())
      }
      if (profileImageFile) {
        formData.append("profile_image", profileImageFile)
      }

      const response = await post(endpoints.updateBasicProfile, formData, {
        skipErrorToast: true,
      })

      if (response?.data?.success) {
        // Refetch /me so sidebar gets full profile_media (update payload can be partial).
        const meRes = await get(endpoints.getMe, {
          params: { "with[]": [...ADMIN_ME_WITH, "addresses.city"] },
          skipErrorToast: true,
        })
        const user =
          meRes?.data?.success && meRes.data.result
            ? meRes.data.result
            : response.data.result

        if (user) {
          const avatarUrl = getUserAvatarUrl(user)
          dispatch(setUser(user))
          if (avatarUrl) dispatch(setUserPic(avatarUrl))
          setProfile(mapUserToProfile(user))
          setPhotoPreview(avatarUrl || null)
        }
        setProfileImageFile(null)
        toast.success(
          response.data.message || "Profile updated successfully.",
          TOAST_OPTIONS,
        )
      } else if (response) {
        const apiFieldErrors = getApiFieldErrors(response, {
          first_name: "firstName",
          last_name: "lastName",
          phone_number: "phone",
          profile_image: "photo",
        })
        if (Object.keys(apiFieldErrors).length) {
          setFieldErrors(apiFieldErrors)
        } else {
          toastApiError(response, "Failed to update profile")
        }
      }
    } catch (err) {
      toastApiError(err, "Failed to update profile")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-page admin-settings">
      <PageHeader
        className="admin-settings__intro"
        title="Settings"
        subtitle="Manage your and platform preferences."
      />

      <div className="admin-settings__toolbar">
        <ListingTabs
          items={SETTINGS_TABS}
          activeKey={activeTab}
          onChange={handleTabChange}
        />
        {activeTab === "profile" ? (
          <div className="admin-settings__actions">
            <CustomButton
              variant="secondary"
              size="md"
              type="button"
              onClick={() => setPasswordModalOpen(true)}
              disabled={saving || loadingProfile}
            >
              Change Password
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              onClick={handleSave}
              loading={saving}
              disabled={saving || loadingProfile}
              trailingIconSrc={ICONS.saveAdd}
              trailingIconAlt=""
            >
              Update Profile
            </CustomButton>
          </div>
        ) : null}
      </div>

      {activeTab === "profile" ? (
        <ProfileSettingsTab
          profile={profile}
          onChange={handleProfileChange}
          photoPreview={photoPreview || avatarFromUser}
          onPhotoChange={handlePhotoChange}
          onManageTwoFactor={handleManageTwoFactor}
          twoFactorLoading={updatingTwoFactor}
          emailDisabled
          fieldErrors={fieldErrors}
        />
      ) : null}

      {activeTab === "notifications" ? (
        <NotificationsTab
          preferences={notificationPrefs}
          onToggle={handleNotificationToggle}
          sections={notificationSections}
          isLoading={loadingNotifications}
          disabled={saving || loadingNotifications}
        />
      ) : null}

      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </div>
  )
}

AdminSettings.propTypes = {
  basePath: PropTypes.string,
  documentTitle: PropTypes.string,
  notificationSections: PropTypes.array,
}

AdminSettings.defaultProps = {
  basePath: "/admin/settings",
  documentTitle: "Settings | AMOK . EARTH Admin",
  notificationSections: NOTIFICATION_SECTIONS,
}

export default AdminSettings
